/* ==========================================================================
   Battery CCA Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-cca]');
  if (!root) return;

  var CI_PER_LITRE = 61.0237;

  /* Cranking amps needed per cubic inch of displacement. The old rule of one
     amp per cubic inch is a bare minimum for a warm gasoline engine. */
  var PER_CI = { gas: 1.0, diesel: 2.0 };

  /* Cold thickens oil and slows the chemistry inside the battery, so the
     requirement goes up exactly as available output goes down. */
  var CLIMATE = { mild: 1.15, cold: 1.45, severe: 1.75 };

  /* Fraction of rated CCA a healthy battery still delivers at temperature. */
  var OUTPUT_AT_TEMP = { mild: 1.00, cold: 0.72, severe: 0.55 };

  var AGE_LOSS_PER_YEAR = 0.04;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    if (!isFinite(input.disp) || input.disp <= 0) {
      return { ok: false, errors: ['Enter the engine displacement.'] };
    }
    var ci = input.dispUnit === 'L' ? input.disp * CI_PER_LITRE : input.disp;
    var perCi = PER_CI[input.fuel] || PER_CI.gas;
    var climate = CLIMATE[input.climate] || CLIMATE.cold;

    var minimum = ci * perCi;
    var recommended = minimum * climate;

    var status, warnings = [], figures = [
      { label: 'Recommended CCA', value: comma(Math.round(recommended / 10) * 10),
        sub: 'Bare minimum is ' + comma(Math.round(minimum)) + '. Buy above the recommendation, never below.' }
    ];

    var rows = [
      { head: 'Requirement' },
      { label: 'Displacement', value: num(ci, 0) + ' ci  (' + num(ci / CI_PER_LITRE, 1) + ' L)' },
      { label: 'Fuel', value: input.fuel === 'diesel' ? 'Diesel' : 'Gasoline' },
      { label: 'Bare minimum', value: comma(Math.round(minimum)) + ' CCA' },
      { label: 'Climate factor', value: num(climate, 2) + 'x' },
      { label: 'Recommended', value: comma(Math.round(recommended / 10) * 10) + ' CCA' }
    ];

    if (isFinite(input.have) && input.have > 0) {
      var age = isFinite(input.age) && input.age > 0 ? input.age : 0;
      var ageFactor = Math.max(0.3, 1 - age * AGE_LOSS_PER_YEAR);
      var tempFactor = OUTPUT_AT_TEMP[input.climate] || 1;
      var effective = input.have * ageFactor;
      var atTemp = effective * tempFactor;

      rows.push({ head: 'Your battery' });
      rows.push({ label: 'Rated', value: comma(Math.round(input.have)) + ' CCA' });
      if (age > 0) rows.push({ label: 'After ' + num(age, 1) + ' years', value: comma(Math.round(effective)) + ' CCA' });
      rows.push({ label: 'Available when cold', value: comma(Math.round(atTemp)) + ' CCA' });
      rows.push({ label: 'Needed', value: comma(Math.round(minimum)) + ' CCA' });

      figures.push({ label: 'Yours when cold', value: comma(Math.round(atTemp)) + ' CCA',
        sub: age > 0 ? 'Rated ' + comma(Math.round(input.have)) + ', reduced for ' + num(age, 1) + ' years of age and for temperature.'
                     : 'Rated ' + comma(Math.round(input.have)) + ', reduced for temperature.' });

      if (atTemp < minimum) {
        status = { key: 'now', label: 'Underpowered',
                   note: 'On a cold morning this battery delivers about ' + comma(Math.round(atTemp))
                       + ' CCA against a ' + comma(Math.round(minimum)) + ' CCA requirement. Expect slow cranking or no start.' };
      } else if (atTemp < recommended * 0.85) {
        status = { key: 'soon', label: 'Marginal',
                   note: 'It will start the engine, but there is very little margin left for a colder night or another year of age.' };
      } else {
        status = { key: 'good', label: 'Adequate',
                   note: 'Comfortably above the requirement even allowing for cold and age.' };
      }
      if (age >= 5) {
        warnings.push('Five years is around the end of a typical battery life. Have it load tested rather than waiting for the morning it does not start.');
      }
    } else {
      status = { key: 'good', label: comma(Math.round(recommended / 10) * 10) + ' CCA',
                 note: 'Minimum ' + comma(Math.round(minimum)) + ' CCA for a ' + num(ci / CI_PER_LITRE, 1) + 'L '
                     + (input.fuel === 'diesel' ? 'diesel' : 'gasoline') + '. Buy at or above the recommendation.' };
    }

    rows.push({ head: 'What cold costs you' });
    rows.push({ label: 'At 80F', value: '100% of rated' });
    rows.push({ label: 'At 32F', value: 'about 80%' });
    rows.push({ label: 'At 0F', value: 'about 60%' });
    rows.push({ label: 'Cranking effort at 0F', value: 'roughly double' });

    return { ok: true, errors: [], warnings: warnings, status: status, figures: figures, rows: rows };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "disp",
          "key": "d",
          "type": "number"
      },
      {
          "name": "dispUnit",
          "key": "du",
          "type": "select"
      },
      {
          "name": "fuel",
          "key": "f",
          "type": "select"
      },
      {
          "name": "climate",
          "key": "c",
          "type": "select"
      },
      {
          "name": "have",
          "key": "h",
          "type": "number"
      },
      {
          "name": "age",
          "key": "a",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-cca__figure">'
        + '<div class="pc-cca__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-cca__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-cca__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-cca__row pc-cca__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-cca__row">'
        + '<span class="pc-cca__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-cca__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_CCA = { compute: compute };
})();
